/**
 * iter-121.113 · Error boundary around LockedKpiStrip.
 *
 * Why: iter-206 found a P0 TDZ ReferenceError in LockedKpiStrip that
 * white-screened EVERY prebuilt dashboard for ALL users. React error
 * boundaries don't propagate through normal rendering — without an
 * explicit boundary, a render-time exception in LockedKpiStrip
 * cascaded to the route-level error handler (the generic "Something
 * went wrong" page), losing all other dashboard content (filters,
 * DashboardGrid, etc.).
 *
 * This boundary localizes the failure: if LockedKpiStrip ever crashes
 * again, the rest of the route (toolbar, DashboardGrid, drill-downs)
 * stays interactive and the user gets a useful fallback message
 * pointing them to a refresh + retry path.
 *
 * Standard React 18+ class-component pattern. Functional components
 * cannot be error boundaries (no `getDerivedStateFromError`).
 */
import React from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";


export default class LockedKpiStripBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, message: null };
    }

    static getDerivedStateFromError(error) {
        return {
            hasError: true,
            message: (error && (error.message || String(error))) || "Unknown render error",
        };
    }

    componentDidCatch(error, info) {
        // Log to console for the user / support to read. Avoid sending
        // unbounded stack traces to the backend — the user can grab
        // them from DevTools if needed.
        console.error("[LockedKpiStripBoundary] crash:", error, info);
    }

    handleRetry = () => {
        this.setState({ hasError: false, message: null });
    };

    render() {
        if (this.state.hasError) {
            return (
                <div className="border border-destructive/40 rounded-md bg-destructive/5 p-6 my-6"
                     data-testid="locked-kpi-strip-error-boundary">
                    <div className="flex items-start gap-3">
                        <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold">
                                Dashboard header failed to render
                            </div>
                            <div className="text-xs text-muted-foreground mt-1 break-words">
                                {this.state.message}
                            </div>
                            <div className="text-xs text-muted-foreground mt-2">
                                The rest of this page (filters, widgets, drill-downs) is still available. Try a refresh
                                — if the problem persists, switch to the Gallery preview and let support know.
                            </div>
                            <div className="flex gap-2 mt-3">
                                <Button size="sm" variant="outline" className="rounded-sm h-7"
                                        onClick={this.handleRetry}
                                        data-testid="locked-kpi-strip-boundary-retry">
                                    <RefreshCw className="h-3 w-3 mr-1.5" />
                                    Retry render
                                </Button>
                                <Button size="sm" variant="ghost" className="rounded-sm h-7"
                                        onClick={() => window.location.reload()}
                                        data-testid="locked-kpi-strip-boundary-reload">
                                    Reload page
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }
        return this.props.children;
    }
}
